import React from 'react';
import { Link } from 'react-router-dom';
import { DataFlowWordmark } from './DataFlowWordmark';

const CONTACT_EMAIL = 'contato@workflowbrasil.com';
const year = new Date().getFullYear();

interface FooterLinkItem {
  label: string;
  href: string;
  route?: boolean;
  external?: boolean;
}

interface FooterColumn {
  heading: string;
  links: FooterLinkItem[];
}

const columns: FooterColumn[] = [
  {
    heading: 'Soluções',
    links: [
      { label: 'Torre de Controle Operacional', href: '#solutions' },
      { label: 'Modelagem Visual de Fluxos', href: '#solutions' },
      { label: 'Gestão da Rotina', href: '#solutions' },
      { label: 'Resolução de Falhas', href: '#solutions' },
    ],
  },
  {
    heading: 'Estratégia & Governança',
    links: [
      { label: 'Desdobramento Estratégico', href: '#solutions' },
      { label: 'Score de Performance', href: '#score' },
      { label: 'Painel de Autoridade', href: '#score' },
      { label: 'Simulação de Cenários', href: '#platform' },
    ],
  },
  {
    heading: 'Infraestrutura',
    links: [
      { label: 'Organograma & Escopo', href: '#platform' },
      { label: 'Governança & RBAC', href: '#platform' },
      { label: 'Integrações Nativas', href: '#platform' },
      { label: 'Excelência Individual', href: '#score' },
    ],
  },
  {
    heading: 'Institucional',
    links: [
      { label: 'Sobre o DataFlow', href: '#inicio' },
      { label: 'Plataforma Enterprise', href: '#platform' },
      { label: 'Segurança & SLA', href: '#security' },
      { label: 'Demonstração Técnica', href: '#contact' },
    ],
  },
  {
    heading: 'Compliance',
    links: [
      { label: 'Política de Privacidade', href: '/privacidade', route: true },
      { label: 'Termos de Uso', href: '/termos', route: true },
      { label: 'LGPD — Base Legal', href: '#security' },
      { label: 'ISO 27001 & SOC 2', href: '#security' },
    ],
  },
];

function FooterLink({ label, href, route, external }: FooterLinkItem) {
  if (route) {
    return (
      <Link to={href} className="text-sm text-slate-600 transition-colors hover:text-slate-900 xl:text-[0.8125rem] xl:leading-snug">
        {label}
      </Link>
    );
  }
  if (external) {
    return (
      <a href={href} className="text-sm text-slate-600 transition-colors hover:text-slate-900 xl:text-[0.8125rem] xl:leading-snug">
        {label}
      </a>
    );
  }
  return (
    <a
      href={href}
      onClick={(e) => {
        if (href.startsWith('#')) {
          e.preventDefault();
          document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
        }
      }}
      className="text-sm text-slate-600 transition-colors hover:text-slate-900 xl:text-[0.8125rem] xl:leading-snug"
    >
      {label}
    </a>
  );
}

function FooterColumnBlock({ heading, links }: FooterColumn) {
  return (
    <div className="min-w-0 xl:px-1">
      <p className="mb-3 text-[0.65rem] font-bold uppercase tracking-widest text-slate-500 xl:mb-3.5 xl:text-[0.62rem]">
        {heading}
      </p>
      <ul className="flex flex-col gap-2 xl:gap-2">
        {links.map((link) => (
          <li key={link.label}>
            <FooterLink {...link} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t border-slate-200 bg-slate-100/80">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 py-14 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[2.1fr_repeat(5,minmax(0,1fr))] xl:gap-6 xl:py-16">
          {/* Brand */}
          <div className="min-w-0 xl:pr-3">
            <div className="mb-5">
              <DataFlowWordmark size="md" className="xl:hidden" />
              <DataFlowWordmark size="lg" className="hidden xl:inline-flex" />
            </div>
            <p className="mb-5 text-sm leading-relaxed text-slate-500 sm:text-[0.9375rem] xl:text-base xl:leading-7">
              Plataforma enterprise de engenharia, simulação e governança de processos operacionais e
              administrativos.
            </p>
            <div className="mb-6 flex flex-col gap-2">
              <span className="text-xs text-slate-500 sm:text-sm">São Paulo, SP — Brasil</span>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-xs text-slate-500 transition-colors hover:text-slate-700 sm:text-sm"
              >
                {CONTACT_EMAIL}
              </a>
            </div>
            <div className="flex flex-wrap gap-2">
              {['ISO 27001', 'LGPD', 'SOC 2'].map((b) => (
                <span
                  key={b}
                  className="rounded border border-slate-300 px-2.5 py-0.5 text-[0.68rem] font-bold text-slate-600 sm:text-xs"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>

          {columns.map((col) => (
            <FooterColumnBlock key={col.heading} {...col} />
          ))}
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-slate-200 py-6 md:flex-row">
          <p className="text-xs text-slate-500">
            © {year} DataFlow. Todos os direitos reservados.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {['ISO 27001', 'SOC 2 Type II', 'LGPD', '99.97% SLA', 'AES-256'].map((badge) => (
              <span
                key={badge}
                className="rounded border border-slate-300 px-2 py-0.5 text-[0.7rem] font-semibold text-slate-500"
              >
                {badge}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
